import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import {
  IIACase,
  IIACaseCreateRequest,
  IIACaseUpdateRequest,
  IIACaseRelatedRecord,
  IIACaseReportResponse,
  IFile,
  IMasterNamePerson,
} from '@/models';

import { http } from './http';
import { IGetParams, IIndeterminatePaginationResponse } from './types';

export const InternalAffairsApi = {
  list: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IIndeterminatePaginationResponse<IIACase>>> => {
    return http.get(`api/${ApiVersion.V1}/internal-affairs`, { params });
  },

  getOne: async (id: string): Promise<AxiosResponse<IIACase>> => {
    return http.get(`api/${ApiVersion.V1}/internal-affairs/${id}`);
  },

  create: async (
    data: IIACaseCreateRequest,
  ): Promise<AxiosResponse<IIACase>> => {
    return http.post(`api/${ApiVersion.V1}/internal-affairs`, data);
  },

  update: async (
    id: string,
    data: IIACaseUpdateRequest,
  ): Promise<AxiosResponse<IIACase>> => {
    return http.patch(`api/${ApiVersion.V1}/internal-affairs/${id}`, data);
  },

  delete: async (id: string, reason: string): Promise<AxiosResponse<void>> => {
    return http.delete(`api/${ApiVersion.V1}/internal-affairs/${id}`, {
      data: { reason },
    });
  },

  // File management
  updateFiles: async (
    id: string,
    files: IFile[],
  ): Promise<AxiosResponse<IFile[]>> => {
    return http.patch(`api/${ApiVersion.V1}/internal-affairs/${id}/files`, {
      files,
    });
  },

  // Email files
  emailFiles: async (
    id: string,
    data: {
      emailAddress: string;
      subject?: string;
      emailBody: string;
      files: IFile[];
    },
  ): Promise<AxiosResponse<{ message: string; iaCase: IIACase }>> => {
    return http.post(`api/${ApiVersion.V1}/internal-affairs/${id}/email`, data);
  },

  // Assigned officers management
  addAssignedOfficers: async (
    id: string,
    officerIds: string[],
  ): Promise<AxiosResponse<IIACase>> => {
    return http.post(
      `api/${ApiVersion.V1}/internal-affairs/${id}/assigned-officers`,
      { officerIds },
    );
  },

  removeAssignedOfficers: async (
    id: string,
    officerIds: string[],
  ): Promise<AxiosResponse<IIACase>> => {
    return http.delete(
      `api/${ApiVersion.V1}/internal-affairs/${id}/assigned-officers`,
      { data: { officerIds } },
    );
  },

  // Related records management
  addRelatedRecords: async (
    id: string,
    records: IIACaseRelatedRecord[],
  ): Promise<AxiosResponse<IIACase>> => {
    return http.post(
      `api/${ApiVersion.V1}/internal-affairs/${id}/related-records`,
      { records },
    );
  },

  removeRelatedRecords: async (
    id: string,
    recordIds: string[],
  ): Promise<AxiosResponse<IIACase>> => {
    return http.delete(
      `api/${ApiVersion.V1}/internal-affairs/${id}/related-records`,
      { data: { recordIds } },
    );
  },

  // People involved management
  getPeopleInvolved: async (
    id: string,
    params?: IGetParams,
  ): Promise<
    AxiosResponse<IIndeterminatePaginationResponse<IMasterNamePerson>>
  > => {
    return http.get(
      `api/${ApiVersion.V1}/internal-affairs/${id}/people-involved`,
      { params },
    );
  },

  addPeopleInvolved: async (
    id: string,
    personIds: string[],
  ): Promise<AxiosResponse<IIACase>> => {
    return http.post(
      `api/${ApiVersion.V1}/internal-affairs/${id}/people-involved`,
      { personIds },
    );
  },

  removePeopleInvolved: async (
    id: string,
    personIds: string[],
  ): Promise<AxiosResponse<IIACase>> => {
    return http.delete(
      `api/${ApiVersion.V1}/internal-affairs/${id}/people-involved`,
      { data: { personIds } },
    );
  },

  // Report generation
  generateReport: async (params: {
    filter?: string;
    format: 'pdf' | 'csv';
  }): Promise<AxiosResponse<IIACaseReportResponse>> => {
    return http.get(`api/${ApiVersion.V1}/internal-affairs/report`, { params });
  },

  // Get IA Officers for assignment
  getIAOfficers: async (): Promise<
    AxiosResponse<Array<{ id: string; name: string; email: string }>>
  > => {
    return http.get(`api/${ApiVersion.V1}/internal-affairs/officers`);
  },
};
